import React, { PureComponent } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { reduxForm, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import { banner } from 'actions';
import { Button, Input, Select } from 'common/components';
import { graphQlToFormErrors } from 'common/utils';
import { required, notOneOf } from 'common/form-validators';
import { trim } from 'common/normalizers';
import { BANNER_IDS, ERROR_DEFAULT, OPTIONS } from 'common/constants';
import { MUTATION_CREATE_CHEMICAL, FETCH_ALL_CHEMICALS } from 'api/activity';
// import mixpanel from 'mixpanel-browser';

export class ChemicalForm extends PureComponent {
  // chemical name field validation
  nameTaken = value => notOneOf(value, this.props.existingChemicalNames);

  render() {
    const {
      onSubmit,
      closeForm,
      error,
      handleSubmit,
      valid,
      pristine,
      submitting,
      selectedChemical,
      activityChemical,
    } = this.props;

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <h2 className={'text--header'}>
          {selectedChemical || activityChemical ? 'EDIT' : 'ADD'} Chemical
        </h2>
        <small className={'text--info'}>* Required</small>
        <Input
          name="name"
          disabled={submitting}
          title="Chemical Name"
          validate={[required, this.nameTaken]}
          normalizeOnBlur={trim}
        />
        <Input
          name="brand"
          disabled={submitting}
          title="Brand"
          normalizeOnBlur={trim}
        />
        <Select
          name="type"
          title="Type"
          validate={[required]}
          options={OPTIONS.CHEMICAL_OPTIONS}
        />

        {error && <p className="text--error">{error}</p>}

        <div className={'field__group'}>
          <fieldset className={'field text--center'}>
            <Button
              type="submit"
              className="btn btn--primary gap-top-small"
              disabled={!valid || pristine || submitting}
            >
              Save
            </Button>
          </fieldset>

          <fieldset className={'field text--center'}>
            <Button
              type="cancel"
              className="btn"
              onClick={closeForm}
              disabled={submitting}
            >
              Cancel
            </Button>
          </fieldset>
        </div>
      </form>
    );
  }
}

ChemicalForm.propTypes = {
  // redux form
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  // map dispatch
  onSubmit: PropTypes.func.isRequired,

  // manually added
  closeForm: PropTypes.func.isRequired,
  changeValue: PropTypes.func,
  selectedChemical: PropTypes.object, // for potential edit chemical
  activityChemical: PropTypes.object, // for activity

  // from graphQL
  loading: PropTypes.bool,
  existingChemicalNames: PropTypes.array,
};

const formSettings = { form: 'chemicalform', enableReinitialize: true };
const mapState = (state, { ownProps }) => {
  let props = {};
  if (ownProps && ownProps.activityChemical) {
    const { brand, type } = ownProps.activityChemical;
    props = {
      ...props,
      initialValues: {
        ...ownProps.activityChemical,
        // because redux-form doesn't prepopulate number 0, but does for string 0
        brand: brand || '',
        type: type || '',
      },
    };
  }
  return props;
};

const mapDispatch = (dispatch, props) => ({
  onSubmit: async values => {
    const { name, brand = '', type = '' } = values;
    let vars = { name, brand, type };

    // just for activity form
    if (props.activityChemical) {
      props.closeForm();
      if (props.changeValue) {
        props.changeValue(
          props.activityChemical.fieldname,
          JSON.stringify(vars)
        );
      }

      // for creating / editing actual chemicals
    } else {
      const res = await props.onCreateChemical(vars);

      if (res && res.data && res.data.createChemical) {
        props.closeForm(res.data.createChemical);
        dispatch(
          banner.display(
            BANNER_IDS.APP,
            `CHEMICAL ${props.id ? 'UPDATED' : 'ADDED'}`,
            false
          )
        );
      } else if (res && res.errors) {
        const _error = res.errors[0] ? res.errors[0].message : ERROR_DEFAULT;
        throw new SubmissionError({ _error });
      } else {
        throw new SubmissionError({ _error: ERROR_DEFAULT });
      }
    }
  },
});

export default compose(
  // for chemical name validation
  graphql(FETCH_ALL_CHEMICALS, {
    options: () => ({
      fetchPolicy: 'cache-and-network',
    }),
    props: props => {
      const {
        data: { getAllChemicals },
        ownProps: { activityChemical },
      } = props;
      let existingChemicalNames = [];
      if (getAllChemicals && getAllChemicals.length) {
        existingChemicalNames = getAllChemicals.map(chemical => chemical.name);
        if (activityChemical) {
          existingChemicalNames = existingChemicalNames.filter(
            chemical => chemical !== activityChemical.name
          );
        }
      }
      return { existingChemicalNames, ...props };
    },
  }),

  graphql(MUTATION_CREATE_CHEMICAL, {
    props: props => ({
      onCreateChemical: chemicalCreateInput => {
        return props
          .mutate({
            variables: { chemicalCreateInput },
            optimisticResponse: {
              createChemical: {
                __typename: 'Chemical',
                id: `CHEMICAL:${chemicalCreateInput.name}`,
                ...chemicalCreateInput,
              },
            },
            update: (cache, { data: { createChemical } }) => {
              // Read the data from our cache for this query.
              const proxyData = cache.readQuery({
                query: FETCH_ALL_CHEMICALS,
              });

              // Replace or add response in list of chemicals.
              // Replacement is necessary since this function is run twice with
              // the real response (https://stackoverflow.com/a/48989517/496046)
              const index = proxyData.getAllChemicals.findIndex(
                item => item.id === createChemical.id
              );
              if (index === -1) {
                proxyData.getAllChemicals.push(createChemical);
              } else {
                proxyData.getAllChemicals.splice(index, 1, createChemical);
              }

              // Write our data back to the cache.
              cache.writeQuery({
                query: FETCH_ALL_CHEMICALS,
                data: proxyData,
              });
            },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  connect(
    mapState,
    mapDispatch
  ),
  reduxForm(formSettings)
)(ChemicalForm);



// WEBPACK FOOTER //
// ./src/containers/ChemicalForm/index.js